import { Stack } from '@mui/joy';
import { createSubfundIdAutocompleteOptions } from 'components/portfolio/fund/SubFundService';
import { calculateAvailableAccountSubAccountIds } from 'components/portfolio/PortfolioService';
import FormMultiAutocomplete from 'components/technical/form/FormMultiAutocomplete';
import FormStaticMultiAutocomplete from 'components/technical/form/FormStaticMultiAutocomplete';
import Loader from 'components/technical/Loader/Loader';
import { usePortfolioFilters } from 'components/technical/PortfolioFilterDrawer/UsePortfolioFilters';
import { usePortfolioFiltersAssetInputQuery, usePortfolioFiltersInputQuery } from 'generated/graphql';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  createAccountIdAutocompleteOptions,
  createSubAccountIdAutocompleteOptions,
} from 'components/portfolio/account/AccountService.tsx';
import {
  createAssetAutocompleteProps,
  type NotVerifiedAssetWithId,
  useAssetPaginatedOptions,
} from 'components/market/asset/AssetService';

export function FormPortfolioFilters() {
  const form = useFormContext();
  const { portfolioFilters } = usePortfolioFilters();

  const subFundIds =
    useWatch({
      control: form.control,
      name: 'subFundIds',
    }) ?? [];

  const accountIds =
    useWatch({
      control: form.control,
      name: 'accountIds',
    }) ?? [];

  const subAccountIds =
    useWatch({
      control: form.control,
      name: 'subAccountIds',
    }) ?? [];

  // run two queries - when calling endpoint with assetIds empty, the backend returns the list of all assets
  const { data, loading } = usePortfolioFiltersInputQuery();

  const { data: assetData, loading: assetLoading } = usePortfolioFiltersAssetInputQuery({
    variables: {
      assetIds: portfolioFilters.assetIds ?? [],
    },
    skip: (portfolioFilters.assetIds ?? []).length === 0,
  });

  const fullData = useMemo(() => {
    return {
      ...data!,
      ...assetData,
    };
  }, [data, assetData]);

  const { subFundAvailableAccountIds, accountAvailableSubAccountIds } = useMemo(() => {
    return calculateAvailableAccountSubAccountIds({
      allSubFunds: fullData?.portfolio?.subFunds?.list ?? [],
      allAccounts: fullData?.portfolio?.accounts ?? [],
      subFundIds,
      accountIds,
      subAccountIds,
    });
  }, [fullData, subFundIds, accountIds, subAccountIds]);

  const subFunds = fullData?.portfolio?.subFunds?.list ?? [];
  const assetOptions = useAssetPaginatedOptions();
  const subAccountOptionsConfig = createSubAccountIdAutocompleteOptions(fullData?.portfolio?.accounts ?? []);
  const filteredByAccSubAccountOptions = subAccountOptionsConfig.options.filter((option) =>
    accountAvailableSubAccountIds.includes(option.value)
  );

  if (loading || assetLoading) {
    return <Loader variant="small" />;
  }

  return (
    <Stack spacing={1.5}>
      <FormStaticMultiAutocomplete
        name={'subFundIds'}
        {...createSubfundIdAutocompleteOptions(subFunds)}
        label={'Sub-fund'}
        width="fullWidth"
      />
      <FormStaticMultiAutocomplete
        disabled={subFundAvailableAccountIds.length === 0}
        name="accountIds"
        {...createAccountIdAutocompleteOptions(
          fullData?.portfolio?.accounts?.filter((acc) => subFundAvailableAccountIds.includes(acc.id)) ?? []
        )}
        label="Account"
        width="fullWidth"
      />
      <FormStaticMultiAutocomplete
        disabled={filteredByAccSubAccountOptions.length === 0}
        name="subAccountIds"
        {...subAccountOptionsConfig}
        options={filteredByAccSubAccountOptions}
        label="Sub-account"
        width="fullWidth"
      />
      <FormMultiAutocomplete
        {...assetOptions}
        {...createAssetAutocompleteProps<NotVerifiedAssetWithId>()}
        width="fullWidth"
        label="Asset"
        name="assets"
        limitTags={3}
      />
    </Stack>
  );
}
